<!-- Notifications toggle -->
<button
    mat-icon-button
    (click)="openPanel()"
    #notificationsOrigin>
    <ng-container *ngIf="unreadCount > 0">
        <span class="absolute top-0 right-0 left-0 flex items-center justify-center h-3">
            <span class="flex items-center justify-center shrink-0 min-w-4 h-4 px-1 ml-4 mt-2.5 rounded-full bg-teal-600 text-primary-50 text-xs font-medium">
                {{unreadCount}}
            </span>
        </span>
    </ng-container>
    <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>

    <ng-container *transloco="let t">
    <div
        class="sai-notifications-panel fixed inset-0 sm:static sm:inset-auto flex flex-col sm:w-[420px] sm:min-w-[420px] sm:rounded-2xl overflow-hidden shadow-lg"
        [attr.dir]="lang === 'ar' ? 'rtl' : 'ltr'">

        <!-- Header - keep primary background; justify-between (not ml-auto) so RTL puts title at inline-start and trash at inline-end -->
        <div class="flex shrink-0 items-center justify-between gap-3 py-4 pe-4 ps-6 bg-primary hover:hover-pg-primary text-on-primary">
            <div class="flex items-center gap-3 min-w-0">
                <div class="sm:hidden -ms-1">
                    <button
                        mat-icon-button
                        (click)="closePanel()">
                        <mat-icon
                            class="icon-size-5 text-current"
                            [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                    </button>
                </div>
                <div class="text-lg font-semibold leading-10">{{ t('Notifications') }}</div>
            </div>
            <button
                mat-icon-button
                [matTooltip]="t('Remove all')"
                [disabled]="!notifications?.length"
                (click)="deleteAllNotifications()">
                <mat-icon
                    class="icon-size-5 text-current"
                    [svgIcon]="'heroicons_outline:trash'"></mat-icon>
            </button>
        </div>

        <!-- Filter tabs -->
        <div class="sai-notifications-panel__surface shrink-0 px-4 pt-4 pb-2">
            <div class="sai-notifications-panel__tablist flex items-center gap-1 p-1 rounded-xl">
                <button
                    type="button"
                    class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                    [class.sai-notifications-panel__tab--active]="activeFilter === 'all'"
                    (click)="setActiveFilter('all')">
                    <span>{{ t('All') }}</span>
                    <span class="sai-notifications-panel__tab-badge">{{ allCount }}</span>
                </button>
                <button
                    type="button"
                    class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                    [class.sai-notifications-panel__tab--active]="activeFilter === 'unread'"
                    (click)="setActiveFilter('unread')">
                    <span>{{ t('Unread') }}</span>
                    <span class="sai-notifications-panel__tab-badge">{{ unreadTabCount }}</span>
                </button>
                <button
                    type="button"
                    class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                    [class.sai-notifications-panel__tab--active]="activeFilter === 'read'"
                    (click)="setActiveFilter('read')">
                    <span>{{ t('Read') }}</span>
                    <span class="sai-notifications-panel__tab-badge">{{ readTabCount }}</span>
                </button>
            </div>
        </div>

        <!-- Notification cards -->
        <div class="sai-notifications-panel__surface relative flex-auto min-h-0 sm:max-h-[28rem] overflow-y-auto overflow-x-hidden px-4 py-2">
            <ng-container *ngFor="let notification of filteredNotifications; trackBy: trackByFn">
                <div
                    class="sai-notifications-panel__card flex-shrink-0 mb-3 rounded-xl border"
                    [class.sai-notifications-panel__card--unread]="!notification.read"
                    [class.sai-notifications-panel__card--read]="notification.read">

                    <div
                        class="flex items-start gap-3 p-3 cursor-pointer min-h-[3.25rem]"
                        (click)="toggleExpand(notification)">

                        <div
                            class="flex shrink-0 items-center justify-center w-9 h-9 rounded-full"
                            [ngClass]="getNotificationIconStyle(notification).bg">
                            <mat-icon
                                class="icon-size-5"
                                [ngClass]="getNotificationIconStyle(notification).text"
                                [svgIcon]="getNotificationIconStyle(notification).icon">
                            </mat-icon>
                        </div>

                        <div class="flex flex-col flex-1 min-w-0 pt-0.5">
                            <div
                                class="notification-panel-text sai-notifications-panel__title font-semibold text-sm leading-snug"
                                [class.line-clamp-2]="!isExpanded(notification)"
                                [innerHTML]="getNotificationTitle(notification)"></div>
                            <div
                                *ngIf="!isExpanded(notification) && notification.description"
                                class="sai-notifications-panel__muted mt-1 text-xs line-clamp-1"
                                [innerHTML]="notification.description"></div>
                        </div>

                        <div class="flex shrink-0 items-center gap-0.5 -mr-1 -mt-0.5">
                            <button
                                type="button"
                                class="sai-notifications-panel__icon-btn"
                                mat-icon-button
                                (click)="toggleExpand(notification); $event.stopPropagation()">
                                <mat-icon
                                    class="icon-size-4"
                                    [svgIcon]="isExpanded(notification) ? 'heroicons_mini:chevron-up' : 'heroicons_mini:chevron-down'"></mat-icon>
                            </button>
                            <button
                                type="button"
                                class="sai-notifications-panel__icon-btn"
                                mat-icon-button
                                (click)="delete(notification); $event.stopPropagation()"
                                [matTooltip]="t('Remove')">
                                <mat-icon
                                    class="icon-size-4"
                                    [svgIcon]="'heroicons_mini:x-mark'"></mat-icon>
                            </button>
                        </div>
                    </div>

                    <div
                        *ngIf="isExpanded(notification)"
                        class="sai-notifications-panel__divider px-3 pb-3 pt-0 ml-12 border-t">
                        <div
                            *ngIf="notification.description"
                            class="sai-notifications-panel__muted text-sm leading-relaxed mt-3 mb-3"
                            [innerHTML]="notification.description"></div>
                        <div class="flex items-center justify-between gap-3">
                            <span class="sai-notifications-panel__muted text-xs">
                                {{notification.time | date:'MMM dd, h:mm a'}}
                            </span>
                            <button
                                type="button"
                                mat-stroked-button
                                class="sai-notifications-panel__okay-btn !min-h-8 !h-8 !px-4 !text-sm !rounded-lg"
                                (click)="acknowledgeNotification(notification, $event)">
                                {{ t('Okay') }}
                            </button>
                        </div>
                    </div>
                </div>
            </ng-container>

            <!-- No notifications -->
            <ng-container *ngIf="!filteredNotifications.length">
                <div class="flex flex-col flex-auto items-center justify-center py-12 px-6">
                    <div class="flex flex-0 items-center justify-center w-14 h-14 rounded-full bg-primary/10">
                        <mat-icon
                            class="text-primary"
                            [svgIcon]="'heroicons_outline:bell'"></mat-icon>
                    </div>
                    <div class="sai-notifications-panel__title mt-5 text-xl font-semibold tracking-tight">{{ t('No notifications') }}</div>
                    <div class="sai-notifications-panel__muted w-full max-w-60 mt-1 text-sm text-center">{{ t('When you have notifications, they will appear here.') }}</div>
                </div>
            </ng-container>
        </div>

        <!-- Footer -->
        <div class="sai-notifications-panel__surface sai-notifications-panel__divider shrink-0 px-4 py-3 border-t">
            <button
                type="button"
                class="sai-notifications-panel__title text-sm font-medium underline underline-offset-2 hover:opacity-80 disabled:opacity-40 disabled:no-underline"
                [disabled]="unreadCount === 0"
                (click)="markAllAsRead()">
                {{ t('Mark all as read') }}
            </button>
        </div>

    </div>

    </ng-container>

</ng-template>
